import { css } from '@Root/lib/lit-3form-block';

export const styles = [
  css`
    .george-layout {
      grid-template-rows:
        var(--block-top-padding)
        minmax(0, auto)
        minmax(0, auto)
        minmax(0, auto)
        var(--block-bottom-padding)
        [last-line];
      max-width: 100%;
    }

    /* The block sets align-items on the grid, which would collapse the shared
       rule inside its grid area; stretch it so the border-bottom lands at the
       block's bottom. */
    .horizontal-rule {
      align-self: stretch;
    }

    h2 {
      padding-bottom: var(--text-stack-header-gap);
    }

    h3 {
      font-size: var(--fs-title-small);
      line-height: var(--lh-title-small);
      letter-spacing: var(--ls-title-small);
      padding-top: calc(14rem / var(--default-rem-px-count));
    }

    img {
      max-width: 100%;
    }

    .header {
      grid-column: 2 / 8;
      grid-row: 2;
      padding-bottom: calc(40rem / var(--default-rem-px-count));
    }

    /* Divider between the header and the card groups. The block-bottom rule
       is the shared .horizontal-rule spanning rows 1 / last-line. */
    .header-rule {
      grid-column: 2 / 8;
      grid-row: 3;
      border-bottom: 1px solid var(--block-rule-color, #c0c0c0);
    }

    .card-groups {
      grid-column: 2 / 8;
      grid-row: 4;
      display: flex;
      flex-wrap: wrap;
      gap: calc(40rem / var(--default-rem-px-count));
      padding-top: var(--between-row-padding);
    }

    .card-group-wrapper {
      display: flex;
      flex-direction: column;
    }

    .image-description {
      padding-top: calc(14rem / var(--default-rem-px-count));
    }

    .image-title {
      /* Pinned at 22/44 until the driver cards get a Figma type spec. */
      padding-top: calc(14rem / var(--default-rem-px-count));
      font-size: calc(22rem / var(--default-rem-px-count));
      line-height: calc(44rem / var(--default-rem-px-count));
      letter-spacing: -0.54px;
      font-weight: var(--fw-regular);
    }

    .image {
      width: 100%;
      aspect-ratio: 3 / 2;
      display: flex;
      justify-content: center;
      align-items: center;
    }

    .text-sections-wrapper {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      justify-content: space-between;
    }

    .text-section {
      padding-top: var(--between-row-padding);
      font-size: var(--fs-body-small);
      line-height: var(--lh-body-small);
      letter-spacing: var(--ls-body-small);
    }

    .text-section-header {
      font-weight: var(--fw-medium);
      font-size: var(--fs-body-small);
      line-height: var(--lh-body-small);
      letter-spacing: var(--ls-body-small);
      margin-bottom: calc(7rem / var(--default-rem-px-count));
    }

    cta-button {
      padding: var(--between-row-padding) 0;
      margin-top: auto;
      /* Two thirds of the card, i.e. 8 of 12 nested columns. */
      max-width: calc(100% * 8 / 12);
    }

    ul {
      margin-block-start: unset;
      margin-block-end: unset;
      padding-inline-start: 1em;

      li {
        font-size: var(--fs-body-small);
      }
    }

    .card-group-header {
      width: 100%;
      font-weight: var(--fw-medium);
    }

    .card-group-drivers {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 0;

      --inner-line: rgba(0, 0, 0, 0.12);
      --cell-pad: calc(16rem / var(--default-rem-px-count));
    }

    /* Cells draw LEFT (vertical) and BOTTOM (horizontal) lines by default;
       the first cell of each row drops its left line per breakpoint below. */
    .card-group-drivers > .driver-wrapper {
      display: flex;
      flex-direction: column;
      padding: var(--cell-pad);
      width: 100%;
      box-sizing: border-box;
      border-left: 1px solid var(--inner-line);
      border-bottom: 1px solid var(--inner-line);
    }

    /* Remove horizontal line on the actual last row (set by JS) */
    .card-group-drivers > .is-last-row {
      border-bottom: none;
    }

    /* 2 columns: rows start at children 1, 3, 5, ... */
    .card-group-drivers > :nth-child(2n + 1) {
      border-left: none;
    }

    @media (min-width: 600px) {
      .george-layout {
        align-items: start;
      }

      h3 {
        padding-top: var(--between-row-padding);
      }

      .header {
        display: contents;
      }

      /* Top-row texts share a first baseline. */
      .header h2 {
        grid-column: 2 / 6;
        grid-row: 2;
        align-self: baseline;
        padding-bottom: var(--between-row-padding);
      }

      .header .description {
        grid-column: 8 / 13;
        grid-row: 2;
        align-self: baseline;
      }

      .header-rule {
        grid-column: 2 / 14;
        padding: var(--between-row-padding) 0;
      }

      .card-groups {
        grid-column: 2 / 14;
        gap: calc(2 * var(--between-column-padding))
          var(--between-column-padding);
        padding-top: calc(30rem / var(--default-rem-px-count));
      }

      .image {
        aspect-ratio: 1 / 1;
      }

      /* 3 columns: reset the 2-column row starts, rows now begin at
         children 1, 4, 7, ... */
      .card-group-drivers {
        grid-template-columns: repeat(3, minmax(0, 1fr));
      }

      .card-group-drivers > :nth-child(2n + 1) {
        border-left: 1px solid var(--inner-line);
      }

      .card-group-drivers > :nth-child(3n + 1) {
        border-left: none;
      }
    }

    @media (min-width: 1024px) {
      .header .description {
        grid-column: 6 / 10;
      }

      /* 4 columns: reset the 3-column row starts, rows now begin at
         children 1, 5, 9, ... */
      .card-group-drivers {
        grid-template-columns: repeat(4, minmax(0, 1fr));
      }

      .card-group-drivers > :nth-child(3n + 1) {
        border-left: 1px solid var(--inner-line);
      }

      .card-group-drivers > :nth-child(4n + 1) {
        border-left: none;
      }
    }
  `,
];
